import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { MenuItem, TextField, Typography } from '@material-ui/core';
import { getAllChargersColumns } from './AssignedChargers';
import { Api } from '../../api';
import credentialStore from '../../state/credentialStore';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';

type ChargerTypeFilter = 'all' | 'public' | 'domicil';

export const AllChargers = observer(() => {
  const location = useLocation();
  const history = useHistory();
  const allChargersProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);
  const [filterParams, setFilterParams] = useState({});

  // Get initial charger type from URL query params
  const getInitialChargerType = (): ChargerTypeFilter => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    if (type === 'public' || type === 'domicil') {
      return type;
    }
    return 'all';
  };

  const [chargerType, setChargerType] = useState<ChargerTypeFilter>(getInitialChargerType);

  const getApiParams = (baseParams: any) => {
    const params = { ...baseParams };
    if (chargerType === 'public') {
      params.isVisible = true;
    } else if (chargerType === 'domicil') {
      params.isVisible = false;
    }
    return params;
  };

  const loadChargers = () => {
    if (credentialStore.isUserAdmin) {
      allChargersProvider.setRequest((offset, sortBy, sortOrder) =>
        Api.charge.listChargers(
          getApiParams({ ...filterParams, skip: offset, take: 20, sortBy, sortOrder }),
        ),
      );
    } else {
      allChargersProvider.setRequest((offset, sortBy, sortOrder) =>
        Api.charge.listChargersForAccount(
          credentialStore.selectedAccount.id,
          getApiParams({
            ...filterParams,
            skip: offset,
            take: 20,
            sortBy,
            sortOrder,
          }),
        ),
      );
    }

    allChargersProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) {
        setSortableFields(sortable);
      }
    });
    allChargersProvider.reloadResource();
  };

  useEffect(() => {
    loadChargers();
  }, [credentialStore.selectedAccount, chargerType]);

  // Update URL when charger type changes (but don't add to history for 'all')
  const handleChargerTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newType = event.target.value as ChargerTypeFilter;
    setChargerType(newType);

    // Update URL without adding to history
    const params = new URLSearchParams(location.search);
    if (newType === 'all') {
      params.delete('type');
    } else {
      params.set('type', newType);
    }
    const newSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : '',
    });
  };

  const filterCallback = (param?: string, val?: string) => {
    const urlParams: any = {};

    if (param && val) {
      urlParams[param] = val;
      setFilterParams(urlParams);
    }

    allChargersProvider.setRequest((offset) => {
      return Api.charge.listChargers({
        ...urlParams,
        skip: offset,
        take: 20,
        sortBy: allChargersProvider.sortBy,
        sortOrder: allChargersProvider.sortOrder,
      });
    });

    allChargersProvider.reloadResource();
  };

  const getLabel = () => {
    switch (chargerType) {
      case 'public':
        return 'Public Charge Points';
      case 'domicil':
        return 'Domicil Charge Points';
      default:
        return 'All Charge Points';
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        rows={allChargersProvider.data}
        columns={getAllChargersColumns(allChargersProvider, sortableFields)}
        label={getLabel()}
        count={allChargersProvider.count}
        onPageOffsetChange={allChargersProvider.onPageChange}
        onSortTypeChange={allChargersProvider.setSort}
        filterParamOptions={[
          { label: 'OCPP ID', value: 'ocppId' },
          { label: 'Location', value: 'location' },
        ]}
        filterCallback={filterCallback}
        rowHeight={60}
        additionalHeaderContent={
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              alignContent: 'center',
            }}
          >
            <Typography>Type</Typography>
            <TextField
              value={chargerType}
              select
              style={{ minWidth: '10em', marginLeft: '0.5em', marginRight: '0.5em' }}
              onChange={handleChargerTypeChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="domicil">Domicil</MenuItem>
            </TextField>
          </div>
        }
      />
    </div>
  );
});
